'use client';

import { c as _c } from "react/compiler-runtime";
import type { Address, ChainId } from '@morpho-org/blue-sdk';
import { parseApiAsset } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { CompactAssetInput } from '@morpho-org/ui-v6/client';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { WITHDRAW_BUNDLE_NOT_AUTHORIZED_MESSAGE } from '@/components/common/BundleAuthorizationCallout/messages';
import { ErrorServiceUnavailable } from '@/components/common/Error';
import { POSITION_MANAGER_DIALOG_OPTIONS } from '@/components/common/PositionManager/dialogOptions';
import VaultV1TransactionInfoCard from '@/components/common/PositionManager/VaultV1TransactionInfoCard';
import SectionErrorBoundary from '@/components/common/SectionErrorBoundary';
import { useVaultV1WithdrawLogic } from '@/components/VaultPage/VaultV1/hooks/useVaultV1WithdrawLogic';
import { useVaultV1ActionPanelData } from '@/components/VaultPage/VaultV1/VaultSimpleActionPanel/useVaultV1ActionPanelData';
import VaultV1OperationShell, { VaultV1FieldError } from '@/components/VaultPage/VaultV1/VaultSimpleActionPanel/VaultV1OperationPanel/VaultV1OperationShell';
import { VaultV1WithdrawReviewDialog } from '@/components/VaultPage/VaultV1/VaultSimpleActionPanel/VaultV1OperationPanel/VaultV1ReviewDialog';
import { getParsedValue } from '@/helpers/formatters';
import PositionManagerWithdrawLoading from './PositionManagerWithdrawLoading';
interface PositionManagerWithdrawContentProps {
  vaultAddress: Address;
  chainId: ChainId;
}
const PositionManagerWithdrawContent = t0 => {
  const $ = _c(49);
  const {
    vaultAddress,
    chainId
  } = t0;
  const {
    toggleDialog
  } = useDialogContext();
  let t1;
  if ($[0] !== chainId || $[1] !== vaultAddress) {
    t1 = {
      vaultAddress,
      chainId
    };
    $[0] = chainId;
    $[1] = vaultAddress;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    vaultData,
    isLoading: isQueryLoading,
    error: queryError
  } = useVaultV1ActionPanelData(t1);
  const t2 = vaultData?.asset;
  let t3;
  if ($[3] !== t2) {
    t3 = getParsedValue(t2, parseApiAsset);
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const vaultAsset = t3;
  let t4;
  if ($[5] !== vaultAddress || $[6] !== vaultData) {
    t4 = {
      vaultData,
      vaultAddress
    };
    $[5] = vaultAddress;
    $[6] = vaultData;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const {
    inputAmount,
    setInputAmount,
    isMax,
    onMaxClick,
    maxWithdraw,
    finalPosition,
    isLoading,
    disabledLabel,
    canTransfer,
    bundleCanTransfer,
    tooltipMessage,
    userVaultAssets,
    error,
    resetInput,
    refetchPosition
  } = useVaultV1WithdrawLogic(t4);
  let t5;
  if ($[8] !== refetchPosition || $[9] !== resetInput) {
    t5 = () => {
      resetInput();
      refetchPosition();
    };
    $[8] = refetchPosition;
    $[9] = resetInput;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const onTxSuccess = t5;
  let t6;
  if ($[11] !== inputAmount || $[12] !== isMax || $[13] !== onTxSuccess || $[14] !== toggleDialog || $[15] !== userVaultAssets || $[16] !== vaultAddress || $[17] !== vaultData) {
    t6 = () => {
      if (!isDefined(userVaultAssets)) {
        return;
      }
      toggleDialog(<SectionErrorBoundary fallback={<ErrorServiceUnavailable minHeight="494px" />}><VaultV1WithdrawReviewDialog vaultData={vaultData} vaultAddress={vaultAddress} inputAmount={inputAmount} isMax={isMax} onTxSuccess={onTxSuccess} entryPoint="position_manager" /></SectionErrorBoundary>, POSITION_MANAGER_DIALOG_OPTIONS);
    };
    $[11] = inputAmount;
    $[12] = isMax;
    $[13] = onTxSuccess;
    $[14] = toggleDialog;
    $[15] = userVaultAssets;
    $[16] = vaultAddress;
    $[17] = vaultData;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const openReviewDialog = t6;
  if (isQueryLoading) {
    let t7;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <PositionManagerWithdrawLoading />;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    return t7;
  }
  if (queryError || !vaultData || !vaultAsset) {
    let t7;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <ErrorServiceUnavailable minHeight="400px" />;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    return t7;
  }
  const t7 = `Withdraw ${vaultAsset.symbol}`;
  const t8 = maxWithdraw?.value;
  let t9;
  if ($[21] !== inputAmount || $[22] !== isLoading || $[23] !== isMax || $[24] !== onMaxClick || $[25] !== setInputAmount || $[26] !== t7 || $[27] !== t8 || $[28] !== vaultAsset) {
    t9 = <CompactAssetInput header={t7} amount={inputAmount} token={vaultAsset} onChange={setInputAmount} maxValue={t8} onMaxClick={onMaxClick} isMax={isMax} loading={isLoading} />;
    $[21] = inputAmount;
    $[22] = isLoading;
    $[23] = isMax;
    $[24] = onMaxClick;
    $[25] = setInputAmount;
    $[26] = t7;
    $[27] = t8;
    $[28] = vaultAsset;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== error.userVaultPositionError) {
    t10 = <VaultV1FieldError error={error.userVaultPositionError} message="Error fetching vault position." />;
    $[30] = error.userVaultPositionError;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== finalPosition || $[33] !== userVaultAssets || $[34] !== vaultAddress || $[35] !== vaultAsset || $[36] !== vaultData) {
    t11 = <VaultV1TransactionInfoCard vaultData={vaultData} vaultAddress={vaultAddress} vaultAsset={vaultAsset} initialPosition={userVaultAssets} finalPosition={finalPosition} />;
    $[32] = finalPosition;
    $[33] = userVaultAssets;
    $[34] = vaultAddress;
    $[35] = vaultAsset;
    $[36] = vaultData;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  const t12 = !bundleCanTransfer;
  let t13;
  if ($[38] !== canTransfer || $[39] !== disabledLabel || $[40] !== isLoading || $[41] !== openReviewDialog || $[42] !== t10 || $[43] !== t11 || $[44] !== t12 || $[45] !== t9 || $[46] !== tooltipMessage || $[47] !== vaultAsset.address) {
    t13 = <VaultV1OperationShell assetInput={t9} fieldErrors={t10} summary={t11} assetAddress={vaultAsset.address} canTransfer={canTransfer} isBundleNotAuthorized={t12} bundleNotAuthorizedMessage={WITHDRAW_BUNDLE_NOT_AUTHORIZED_MESSAGE} handleFinalize={openReviewDialog} isLoading={isLoading} reviewLabel="Review Withdraw" disabledLabel={disabledLabel} tooltipMessage={tooltipMessage} />;
    $[38] = canTransfer;
    $[39] = disabledLabel;
    $[40] = isLoading;
    $[41] = openReviewDialog;
    $[42] = t10;
    $[43] = t11;
    $[44] = t12;
    $[45] = t9;
    $[46] = tooltipMessage;
    $[47] = vaultAsset.address;
    $[48] = t13;
  } else {
    t13 = $[48];
  }
  return t13;
};
export default PositionManagerWithdrawContent;